import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Autocomplete, InputAdornment, IconButton } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import PaymentBehaviorModal from './payment-behavior-modal';
import Tooltip from '../ui/tooltip';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { useToast } from '../../providers/toaster-provider';
import countriesData from '../../files/json/countries.json';

export interface Client {
    id?: number;
    company: string;
    companyAbbreviation: string;
    recipient: string;
    streetAddress1: string;
    streetAddress2?: string;
    suburb: string;
    state: string;
    postalCode: string;
    country: string;
    phone?: string;
    fax?: string;
    email?: string;
    nextInvoiceNumber?: string;
    provisionDateOffset?: number;
    isProvisionDateManual?: boolean;
    // A text pattern that identifies this client's real bank transactions by
    // description (e.g. "Fast Transfer from") - boosts matching candidates
    // in a transaction's Xero tab when its description contains this (see
    // getXeroPaymentCandidates, transaction-controller.js).
    searchString?: string;
}

interface AddClientPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: Client) => void;
    isEdit?: { id: number | null, state: boolean };
}

const AddClientPopup: React.FC<AddClientPopupProps> = ({ open, onClose, onCreate, isEdit }) => {
    const { user } = useUser();
    const { showError, showSuccess } = useToast();
    
    // Form state
    const [company, setCompany] = useState('');
    const [companyAbbreviation, setCompanyAbbreviation] = useState('');
    const [recipient, setRecipient] = useState('');
    const [streetAddress1, setStreetAddress1] = useState('');
    const [streetAddress2, setStreetAddress2] = useState('');
    const [suburb, setSuburb] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('');
    const [phone, setPhone] = useState('');
    const [fax, setFax] = useState('');
    const [email, setEmail] = useState('');
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('000001');
    const [searchString, setSearchString] = useState('');
    const [provisionDateOffset, setProvisionDateOffset] = useState('7');
    const [isProvisionDateManual, setIsProvisionDateManual] = useState(false);
    const [isHoveringUnit, setIsHoveringUnit] = useState(false);
    const [openPaymentBehaviorModal, setOpenPaymentBehaviorModal] = useState(false);
    const [paymentBehavior, setPaymentBehavior] = useState<any>(null);
    
    const [errors, setErrors] = useState<{
        company?: string,
        companyAbbreviation?: string,
        recipient?: string,
        streetAddress1?: string,
        suburb?: string,
        state?: string,
        postalCode?: string,
        country?: string,
        phone?: string,
        email?: string,
        nextInvoiceNumber?: string,
        provisionDateOffset?: string
    }>({});

    // API hooks
    const [fetchClient, setFetchClient] = useApi();
    const [saveClient, setSaveClient] = useApi();
    const [fetchPaymentFrequency, setFetchPaymentFrequency] = useApi();
    const [fetchInvoiceSettings, setFetchInvoiceSettings] = useApi();
    const [recompute, setRecompute] = useApi();
    const [analyzeSearchString, setAnalyzeSearchString] = useApi();

    // Dynamically generate country options from JSON
    const countryOptions: DropdownOption[] = useMemo(() => {
        return countriesData.map((country: any) => ({
            value: country.code2,
            label: country.name
        }));
    }, []);

    // Dynamically generate states/provinces map from JSON - only for US and Switzerland
    const countriesWithStates: Record<string, DropdownOption[]> = useMemo(() => {
        const map: Record<string, DropdownOption[]> = {};
        const allowedCountries = ['US', 'CH']; // Only US and Switzerland
        
        countriesData.forEach((country: any) => {
            if (allowedCountries.includes(country.code2) && country.states && country.states.length > 0) {
                map[country.code2] = country.states.map((state: any) => ({
                    value: state.code,
                    label: state.name
                }));
            }
        });
        return map;
    }, []);

    // Get state options for the selected country
    const stateOptions = country && countriesWithStates[country] ? countriesWithStates[country] : [];
    const hasStates = stateOptions.length > 0;

    React.useEffect(() => {
        if (!isEdit?.state) return;
        const { url, method } = buildApiRequestConfig(routes.client.fetchById, [{ param: 'id', value: isEdit?.id }]);
        setFetchClient({ url, method });
    }, [open, user, isEdit]);

    React.useEffect(() => {
        // For a new client, default the provision date to the entity's Invoice Settings value
        if (!open || isEdit?.state || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchInvoiceSettings({ url, method });
    }, [open, user, isEdit]);

    React.useEffect(() => {
        const { data, isLoading } = fetchInvoiceSettings;
        if (data && !isLoading) {
            setProvisionDateOffset(data.provisionDateOffset || '7');
            setIsProvisionDateManual(false);
        }
    }, [fetchInvoiceSettings]);

    React.useEffect(() => {
        if (!openPaymentBehaviorModal || !isEdit?.id) return;
        const { url, method } = buildApiRequestConfig(routes.client.paymentFrequency, [{ param: 'id', value: isEdit?.id }]);
        setFetchPaymentFrequency({ url, method });
    }, [openPaymentBehaviorModal, isEdit, setFetchPaymentFrequency]);

    React.useEffect(() => {
        // Clear state when country changes
        setState('');
    }, [country]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchPaymentFrequency;
        if (data && !isLoading) {
            setPaymentBehavior(data);
        }
    }, [fetchPaymentFrequency]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchClient;
        if (data && !isLoading) {
            setCompany(data.companyName || '');
            setCompanyAbbreviation(data.abbr || '');
            setRecipient(data.recipient || '');
            setStreetAddress1(data.address1 || '');
            setStreetAddress2(data.address2 || '');
            setSuburb(data.suburb || '');
            setState(data.state || '');
            setPostalCode(data.postalCode || '');
            setCountry(data.country || '');
            setPhone(data.phone || '');
            setFax(data.fax || '');
            setEmail(data.email || '');
            setNextInvoiceNumber(data.nextInvoiceNumber || '000001');
            setProvisionDateOffset(data.provisionDateOffset !== null && data.provisionDateOffset !== undefined ? String(data.provisionDateOffset) : '7');
            setIsProvisionDateManual(!!data.isProvisionDateManual);
            setSearchString(data.searchString || '');
        }
        if (error) {
            showError('Failed to fetch client details');
        }
    }, [fetchClient]);

    React.useEffect(() => {
        const { data, isLoading, error } = recompute;
        if (data && !isLoading) {
            setProvisionDateOffset(String(data.client?.provisionDateOffset ?? '7'));
            setIsProvisionDateManual(false);
            showSuccess('Provision date recomputed from payment history');
        }
        if (error) {
            showError('Failed to recompute provision date');
        }
    }, [recompute]);

    React.useEffect(() => {
        const { data, isLoading, error } = analyzeSearchString;
        if (data && !isLoading) {
            if (data.suggestion) {
                setSearchString(data.suggestion);
                showSuccess(`Suggested from ${data.sampleCount} paid transaction${data.sampleCount === 1 ? '' : 's'} - review before saving`);
            } else {
                const reasons: Record<string, string> = {
                    no_invoices: 'This client has no invoices to analyze yet.',
                    no_paid_invoices: 'None of this client\'s invoices have a matching bank transaction yet.',
                    no_descriptions: 'The matching bank transactions have no description to analyze.',
                    no_common_pattern: 'Their paid transactions don\'t share a common description - nothing to suggest.',
                };
                showError(reasons[data.reason] || 'Nothing to suggest yet.');
            }
        }
        if (error) {
            showError('Failed to analyze bank description');
        }
    }, [analyzeSearchString]);

    const validate = () => {
        const newErrors: typeof errors = {};
        
        // Required fields
        if (!company.trim()) newErrors.company = 'Company name is required';
        if (!companyAbbreviation.trim()) newErrors.companyAbbreviation = 'Company abbreviation is required';
        if (companyAbbreviation.length > 4) newErrors.companyAbbreviation = 'Abbreviation cannot exceed 4 characters';
        if (!nextInvoiceNumber.trim()) newErrors.nextInvoiceNumber = 'Next invoice number is required';
        if (nextInvoiceNumber && !/^\d+$/.test(nextInvoiceNumber)) newErrors.nextInvoiceNumber = 'Next invoice number must be numeric';
        if (!recipient.trim()) newErrors.recipient = 'Recipient name is required';
        if (!streetAddress1.trim()) newErrors.streetAddress1 = 'Street address is required';
        if (!suburb.trim()) newErrors.suburb = 'Suburb is required';
        if (hasStates && !state.trim()) newErrors.state = 'State is required';
        if (!postalCode.trim()) newErrors.postalCode = 'Postal code is required';
        if (!country.trim()) newErrors.country = 'Country is required';

        // Email validation
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone validation (basic)
        if (phone && !/^[\d\s\-+()]+$/.test(phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        // Provision date offset validation
        if (provisionDateOffset.trim() && !/^-?\d+$/.test(provisionDateOffset.trim())) {
            newErrors.provisionDateOffset = 'Must be a whole number of days';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            showError('Please fill in all required fields');
            return;
        }

        const clientData: Client = {
            company: company.trim(),
            companyAbbreviation: companyAbbreviation.trim(),
            recipient: recipient.trim(),
            streetAddress1: streetAddress1.trim(),
            streetAddress2: streetAddress2.trim() || undefined,
            suburb: suburb.trim(),
            state,
            postalCode: postalCode.trim(),
            country,
            phone: phone.trim() || undefined,
            fax: fax.trim() || undefined,
            email: email.trim() || undefined,
            nextInvoiceNumber: nextInvoiceNumber.trim() || undefined,
            provisionDateOffset: provisionDateOffset.trim() ? parseInt(provisionDateOffset.trim(), 10) : undefined,
            isProvisionDateManual,
            searchString: searchString.trim() || undefined,
        };

        onCreate(clientData);
    };

    const handleProvisionDateChange = (value: string) => {
        setProvisionDateOffset(value);
        setIsProvisionDateManual(true);
    };

    const handleRecompute = () => {
        if (!isEdit?.id) return;
        const { url, method } = buildApiRequestConfig(routes.client.recomputeProvisionDate, [{ param: 'id', value: isEdit.id }]);
        setRecompute({ url, method });
    };

    const handleAnalyzeSearchString = () => {
        if (!isEdit?.id) return;
        const { url, method } = buildApiRequestConfig(routes.client.analyzeSearchString, [{ param: 'id', value: isEdit.id }]);
        setAnalyzeSearchString({ url, method });
    };

    const handleClose = () => {
        // Reset form
        setCompany('');
        setCompanyAbbreviation('');
        setRecipient('');
        setStreetAddress1('');
        setStreetAddress2('');
        setSuburb('');
        setState('');
        setPostalCode('');
        setCountry('');
        setPhone('');
        setFax('');
        setEmail('');
        setNextInvoiceNumber('000001');
        setProvisionDateOffset('7');
        setIsProvisionDateManual(false);
        setSearchString('');
        setIsHoveringUnit(false);
        setOpenPaymentBehaviorModal(false);
        setPaymentBehavior(null);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} PaperProps={{ sx: { borderRadius: 3, minWidth: 600, p: 0.75 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>
                {isEdit?.state ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {/* Company, Abbreviation and Provision Date */}
                <Box display="flex" gap={2} mb={2}>
                    <Box flex={2}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Company <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Company Name"
                            value={company}
                            onChange={e => setCompany(e.target.value)}
                            error={!!errors.company}
                            helperText={errors.company}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Abbreviation <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Abbreviation"
                            value={companyAbbreviation}
                            onChange={e => setCompanyAbbreviation(e.target.value)}
                            error={!!errors.companyAbbreviation}
                            helperText={errors.companyAbbreviation}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                            inputProps={{ maxLength: 4 }}
                        />
                    </Box>
                    <Box flex={1} minWidth={120}>
                        <Box display="flex" alignItems="center" mb={0.5}>
                            <Typography sx={{ fontWeight: 500, fontSize: 15, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                                Provision Date
                            </Typography>
                            <Tooltip message={
                                isProvisionDateManual
                                    ? 'Manually set - will no longer update automatically based on payment history'
                                    : 'Auto-computed from payment history (or Invoice Settings default until enough history exists)'
                            } />
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Days"
                            value={provisionDateOffset}
                            onChange={e => handleProvisionDateChange(e.target.value)}
                            error={!!errors.provisionDateOffset}
                            helperText={errors.provisionDateOffset}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        onMouseEnter={() => setIsHoveringUnit(true)}
                                        onMouseLeave={() => setIsHoveringUnit(false)}
                                    >
                                        {isEdit?.state && isProvisionDateManual && isHoveringUnit ? (
                                            <IconButton
                                                size="small"
                                                onClick={handleRecompute}
                                                disabled={recompute.isLoading}
                                                title="Reset to Auto-Computed"
                                                sx={{ p: 0.25 }}
                                            >
                                                <RestartAltIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
                                            </IconButton>
                                        ) : (
                                            <Typography
                                                sx={
                                                    isEdit?.state && isProvisionDateManual
                                                        ? { fontSize: 13, color: 'var(--color-primary)', textDecoration: 'underline', cursor: 'pointer' }
                                                        : { fontSize: 13, color: '#9e9e9e' }
                                                }
                                            >
                                                days
                                            </Typography>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>

                {/* Recipient and Next Invoice Number */}
                <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Recipient <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Recipient Name"
                            value={recipient}
                            onChange={e => setRecipient(e.target.value)}
                            error={!!errors.recipient}
                            helperText={errors.recipient}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Next Invoice Number
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter next invoice number"
                            value={nextInvoiceNumber}
                            onChange={e => setNextInvoiceNumber(e.target.value)}
                            error={!!errors.nextInvoiceNumber}
                            helperText={errors.nextInvoiceNumber}
                            InputProps={{
                                startAdornment: companyAbbreviation && (
                                    <InputAdornment position="start" sx={{ mr: 1, fontSize: 14, fontWeight: 500, color: 'var(--color-text-muted)' }}>
                                        {companyAbbreviation}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>

                {/* Bank Description Search String - a bank-memo phrase this client's real
                transactions tend to carry, used to help match their transactions by
                description (e.g. in a transaction's Xero tab). Available regardless of
                whether the entity has Xero connected. */}
                <Box mb={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Box display="flex" alignItems="center">
                            <Typography sx={{ fontWeight: 500, fontSize: 15, color: 'var(--color-text-muted)' }}>
                                Bank Description Search String
                            </Typography>
                            <Tooltip message={'Text this client\'s real bank transactions tend to carry in their description (e.g. "Fast Transfer from") - used to help surface the right matches when linking this client\'s transactions, since a bank memo rarely resembles the client\'s own invoices.'} />
                        </Box>
                        {isEdit?.id && (
                            <Button
                                size="small"
                                onClick={handleAnalyzeSearchString}
                                disabled={analyzeSearchString.isLoading}
                                title="Suggest this from the client's own paid transactions"
                                sx={{ fontSize: 13, textTransform: 'none', color: 'var(--color-primary)', minWidth: 0, p: 0 }}
                            >
                                {analyzeSearchString.isLoading ? 'Analyzing…' : 'Analyze'}
                            </Button>
                        )}
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Fast Transfer from"
                        value={searchString}
                        onChange={e => setSearchString(e.target.value)}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Street Address Line 1 */}
                <Box mb={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Street Address Line 1 <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter PO Box or Street Address"
                        value={streetAddress1}
                        onChange={e => setStreetAddress1(e.target.value)}
                        error={!!errors.streetAddress1}
                        helperText={errors.streetAddress1}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Street Address Line 2 */}
                <Box mb={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Street Address Line 2
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Unit, Suite, or Building name (optional)"
                        value={streetAddress2}
                        onChange={e => setStreetAddress2(e.target.value)}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                {/* Suburb and State */}
                <Box display="flex" gap={2} mb={2}>
                    <Box flex={hasStates ? 1 : 1} sx={{ width: '100%' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Suburb <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Suburb"
                            value={suburb}
                            onChange={e => setSuburb(e.target.value)}
                            error={!!errors.suburb}
                            helperText={errors.suburb}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    {hasStates && (
                        <Box flex={1}>
                            <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                                State <span style={{ color: '#F44336' }}>*</span>
                            </Typography>
                            <Autocomplete
                                options={stateOptions}
                                getOptionLabel={(option) => option.label}
                                value={stateOptions.find(s => s.value === state) || null}
                                onChange={(e, value) => setState(value?.value || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select state"
                                        error={!!errors.state}
                                        helperText={errors.state}
                                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                                    />
                                )}
                                noOptionsText="No states found"
                            />
                        </Box>
                    )}
                </Box>

                {/* Postal Code and Country */}
                <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Postal Code <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Postcode"
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value)}
                            error={!!errors.postalCode}
                            helperText={errors.postalCode}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Country <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <Autocomplete
                            options={countryOptions}
                            getOptionLabel={(option) => option.label}
                            value={countryOptions.find(c => c.value === country) || null}
                            onChange={(e, value) => setCountry(value?.value || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select country"
                                    error={!!errors.country}
                                    helperText={errors.country}
                                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                                />
                            )}
                            noOptionsText="No countries found"
                        />
                    </Box>
                </Box>

                {/* Phone and Fax */}
                <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Phone
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter mobile or landline number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Fax
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter fax number (if applicable)"
                            value={fax}
                            onChange={e => setFax(e.target.value)}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>

                {/* Email */}
                <Box mb={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Email
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                        onClick={handleClose} 
                        variant="contained" 
                        sx={{ 
                            background: 'var(--color-container-background)', 
                            color: 'var(--color-text-muted)', 
                            boxShadow: 'none', 
                            borderRadius: 2, 
                            fontWeight: 600, 
                            width: 120, 
                            '&:hover': { background: 'var(--color-container-background)' } 
                        }}
                    >
                        Cancel
                    </Button>
                    {isEdit?.state && (
                        <Button 
                            onClick={() => setOpenPaymentBehaviorModal(true)}
                            variant="outlined"
                            sx={{ 
                                borderColor: 'var(--color-primary)',
                                color: 'var(--color-primary)', 
                                boxShadow: 'none', 
                                borderRadius: 2, 
                                fontWeight: 600,
                                '&:hover': { borderColor: 'var(--color-primary)', background: 'transparent' } 
                            }}
                        >
                            View Payment Behavior
                        </Button>
                    )}
                </Box>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    sx={{ 
                        background: 'var(--color-primary)', 
                        color: '#fff', 
                        boxShadow: 'none', 
                        borderRadius: 2, 
                        fontWeight: 600, 
                        width: 150, 
                        whiteSpace: 'nowrap',
                        '&:hover': { background: 'var(--color-primary)' } 
                    }}
                >
                    {isEdit?.state ? 'Save Changes' : 'Add Client'}
                </Button>
            </DialogActions>
            
            {/* Payment Behavior Modal */}
            <PaymentBehaviorModal
                open={openPaymentBehaviorModal}
                onClose={() => setOpenPaymentBehaviorModal(false)}
                paymentBehavior={paymentBehavior}
                isLoading={fetchPaymentFrequency.isLoading}
                noData={fetchPaymentFrequency.error === 'No payment frequency analysis found for this client'}
                error={!!fetchPaymentFrequency.error && fetchPaymentFrequency.error !== 'No payment frequency analysis found for this client'}
            />
        </Dialog>
    );
};


export default AddClientPopup;
